import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
	ChatModel,
	ChatParams,
	chatSocketSlice,
	MessageModel,
	QuoteModel,
	RatingModelParams,
	SentFile,
	UploadType,
} from 'Pages/chat'
import { combineReducers } from 'redux'
import { RequestParams, SettingModel } from 'Models'

export type ChatState = {
	currentChat: ChatModel | null
	messages: MessageModel[] | null
	totalMessages: number
	isLoading: boolean
	params: ChatParams
	isReaded: boolean
	serviceMessage: boolean
	ticketScoreId: number | null
	viewRating: boolean
	chatFiles: File[] | null
	filesInProgress: SentFile[] | null
	foundMessages: MessageModel[] | null
	currentFoundMessage: MessageModel | null
	awaitedFileNames: Record<number, string>
	chatSettings: SettingModel | null
	chatToken: string | null
	currentQuote: QuoteModel | null
	foundQuoteMessage: MessageModel | null
}

export const ChatInitialState: ChatState = {
	currentChat: null,
	isLoading: false,
	messages: null,
	params: null,
	totalMessages: null,
	isReaded: false,
	serviceMessage: false,
	ticketScoreId: null,
	viewRating: false,
	chatFiles: null,
	filesInProgress: null,
	foundMessages: null,
	currentFoundMessage: null,
	awaitedFileNames: {},
	chatSettings: null,
	chatToken: null,
	currentQuote: null,
	foundQuoteMessage: null,
}

export namespace ChatNS {
	export type SetSessionToken = PayloadAction<string>
	export type GetChats = PayloadAction<{
		token: string
		section_slug: string
		section_name: string
		order_id?: string
	}>
	export type SetChats = PayloadAction<ChatModel>
	export type SetIsLoading = PayloadAction<boolean>
	export type GetMessages = PayloadAction<{ token: string; params: ChatParams; clear?: boolean }>
	export type SetMessages = PayloadAction<MessageModel[]>
	export type SetParams = PayloadAction<ChatParams>
	export type SetTotalMessages = PayloadAction<number>
	export type SetSoketMessages = PayloadAction<MessageModel>
	export type setUnreaded = PayloadAction<boolean>
	export type setIsReadedMessage = PayloadAction<{
		message_ids: number[]
		chat_id: number | null
		token: string
	}>
	export type setServiceMessage = PayloadAction<boolean>
	export type setRatingMessage = PayloadAction<RatingModelParams>
	export type setTicketScoreId = PayloadAction<number>
	export type setViewRating = PayloadAction<boolean>
	export type uploadFileAction = PayloadAction<UploadType>
	export type getFiles = PayloadAction<{
		token: string
		chat_id: number
		page: number
		per_page: number
	}>
	export type downloadFile = PayloadAction<{
		token: string
		file_id: number
		file_name: string
	}>
	export type setChatFiles = PayloadAction<File[]>
	export type setFilesInProgress = PayloadAction<SentFile[]>
	export type deleteFileInProgress = PayloadAction<number>
	export type SearchMessages = PayloadAction<{
		token: string
		chat_id: number
		query: string
	}>
	export type SetCurrentFoundMessage = PayloadAction<MessageModel | null>
	export type GetMessagesByScroll = PayloadAction<{ token: string; params: RequestParams }>
	export type SelectNextFoundMessage = PayloadAction<{ token: string; message: MessageModel }>
	export type SetAwaitedFiles = PayloadAction<Record<number, string>>
	export type SetChatSettings = PayloadAction<SettingModel>
	export type SetCurrentQuote = PayloadAction<QuoteModel | null>
	export type SearchMessageById = PayloadAction<{
		token: string
		chat_id: number
		message_id: number
	}>
	export type SetFoundQuoteMessage = PayloadAction<MessageModel | null>
}

export const chatSlice = createSlice({
	name: 'chat',
	initialState: ChatInitialState,
	reducers: {
		setIsLoading: (state, action: ChatNS.SetIsLoading) => {
			state.isLoading = action.payload
		},
		getSessionToken: (state) => {},
		setSessionToken: (state, action: ChatNS.SetSessionToken) => {
			state.chatToken = action.payload
		},
		getChats: (state, action: ChatNS.GetChats) => {},
		setChats: (state, action: ChatNS.SetChats) => {
			state.currentChat = action.payload
		},
		getMessages: (state, action: ChatNS.GetMessages) => {},
		setMessages: (state, action: ChatNS.SetMessages) => {
			state.messages = action.payload
		},
		setSocketMessage: (state, action: ChatNS.SetSoketMessages) => {
			state.messages.push(action.payload)
		},
		setTotalMessages: (state, action: ChatNS.SetTotalMessages) => {
			state.totalMessages = action.payload
		},
		setParams: (state, action: ChatNS.SetParams) => {
			state.params = action.payload
		},
		setUnreaded: (state, action: ChatNS.setUnreaded) => {
			state.isReaded = action.payload
		},
		setIsReadedMessage: (state, action: ChatNS.setIsReadedMessage) => {},
		clearChatsStore: (state) => {
			state.currentChat = null
			state.messages = null
			state.params = null
			state.totalMessages = null
			state.isLoading = false
			state.isReaded = false
			state.serviceMessage = false
			state.ticketScoreId = null
			state.viewRating = false
			state.chatFiles = null
			state.filesInProgress = null
			state.currentQuote = null
		},
		setServiceMessage: (state, action: ChatNS.setServiceMessage) => {
			state.serviceMessage = action.payload
		},
		setRatingMessage: (state, action: ChatNS.setRatingMessage) => {},
		setTicketScoreId: (state, action: ChatNS.setTicketScoreId) => {
			state.ticketScoreId = action.payload
		},
		setViewRating: (state, action: ChatNS.setViewRating) => {
			state.viewRating = action.payload
		},
		uploadFileAction: (state, action: ChatNS.uploadFileAction) => {},
		getFiles: (state, action: ChatNS.getFiles) => {},
		downloadFile: (state, action: ChatNS.downloadFile) => {},
		setChatFiles: (state, action: ChatNS.setChatFiles) => {
			state.chatFiles = action.payload
		},
		setFilesInProgress: (state, action: ChatNS.setFilesInProgress) => {
			state.filesInProgress = action.payload
		},
		setAwaitedFiles: (state, action: ChatNS.SetAwaitedFiles) => {
			state.awaitedFileNames = action.payload
		},
		deleteFileInProgress: (state, action: ChatNS.deleteFileInProgress) => {
			state.filesInProgress = state.filesInProgress?.filter((item) => item.id !== action.payload)
		},
		searchMessages: (state, action: ChatNS.SearchMessages) => {},
		setFoundMessages: (state, action: ChatNS.SetMessages) => {
			state.foundMessages = action.payload
		},
		setCurrentFoundMessage: (state, action: ChatNS.SetCurrentFoundMessage) => {
			state.currentFoundMessage = action.payload
		},
		getMessagesByScroll: (state, action: ChatNS.GetMessagesByScroll) => {},
		selectNextFoundMessage: (state, action: ChatNS.SelectNextFoundMessage) => {},
		getChatSettings: () => {},
		setChatSettings: (state, action: ChatNS.SetChatSettings) => {
			state.chatSettings = action.payload
		},
		setCurrentQuote: (state, action: ChatNS.SetCurrentQuote) => {
			state.currentQuote = action.payload
		},
		searchMessageById: (state, action: ChatNS.SearchMessageById) => {},
		setFoundQuoteMessage: (state, action: ChatNS.SetFoundQuoteMessage) => {
			state.foundQuoteMessage = action.payload
		},
	},
})

export const chat = combineReducers({
	[chatSlice.name]: chatSlice.reducer,
	[chatSocketSlice.name]: chatSocketSlice.reducer,
})
